<template>
  <div class="col-12 mb-3">
    <div class="zone-card card-item">
      <div class="zone-card-content">
        <!-- 헤더: 영역 이름 + 정보 -->
        <div class="zone-header-row" @click.stop="onExpandToggle">
          <div class="zone-title-wrapper" v-touch:hold="onTouchHold">
            <i class="fa-regular fa-house zone-icon"></i>
            <span class="zone-title">{{ props.data.name }}</span>
          </div>
          <div class="zone-header-info">
            <!-- 등록된 방 수 -->
            <div class="zone-info-badge">
              <i class="fa-brands fa-microsoft me-1 text-primary"></i>
              <span>{{ props.data.groups.length }}개</span>
            </div>

            <!-- 설정 모드 -->
            <div class="zone-info-badge" :class="props.data.isEditMode ? 'badge-warning' : 'badge-normal'">
              <i class="fa-solid fa-gear me-1"></i>
              <span>설정 모드 {{ props.data.isEditMode ? 'ON' : 'OFF' }}</span>
            </div>
          </div>
          <div class="zone-expand-toggle">
            <i :class="`fa ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`" style="font-size: 0.8rem;"></i>
          </div>
        </div>

        <!-- 본문: 전체 확장/축소 가능 -->
        <transition name="expand">
          <div v-if="isExpanded" class="zone-card-expandable-content">
            <!-- 본문 -->
            <div class="zone-card-body">
              <!-- 조명제어기 미등록 경고 -->
              <div v-if="!hasController" class="zone-warning">
                <i class="fa-solid fa-triangle-exclamation me-2"></i>
                <span>조명제어기 미등록시에는 네트워크 설정을 할 수 없습니다. 조명제어기 추가 버튼을 눌러 추가 후 설정을 진행하십시요.</span>
              </div>

              <!-- 설정 모드 활성화 경고 -->
              <div v-if="props.data.isEditMode" class="zone-warning">
                <i class="fa-solid fa-triangle-exclamation me-2"></i>
                <span>설정 모드 활성화시에는 앱을 통한 장치 제어가 제한됩니다.</span>
              </div>
            </div>

            <!-- 조명제어기 목록 -->
            <div class="zone-controllers-section">
              <div class="zone-controllers-header">
                <i class="fa-solid fa-microchip me-2 text-primary"></i>
                <span class="zone-controllers-title">조명제어기 목록</span>
                <span class="zone-controllers-count">({{ lightControllers.length }}개)</span>
                <button
                  type="button"
                  class="zone-controllers-refresh"
                  title="새로고침"
                  @click.stop="onRefreshClick"
                >
                  <i class="fa-solid fa-arrow-rotate-right"></i>
                </button>
              </div>
              <div class="zone-controllers-list">
                <div v-if="hasController" class="zone-controllers-items">
                  <div
                    v-for="controller in lightControllers"
                    :key="controller._id"
                    class="zone-controller-item"
                    role="button"
                    tabindex="0"
                    @click="onControllerSelect(controller)"
                    @keyup.enter="onControllerSelect(controller)"
                  >
                    <div class="controller-item-main">
                      <div class="controller-item-info">
                        <div class="controller-item-serial-row">
                          <span class="controller-item-serial">
                            <i class="fa-solid fa-hashtag me-1"></i>
                            {{ controller._id }}
                          </span>
                          <span
                            v-if="getControllerAlias(controller)"
                            class="controller-item-alias"
                          >({{ getControllerAlias(controller) }})</span>
                        </div>
                        <div class="controller-item-model">
                          <i class="fa-solid fa-microchip me-2 text-primary"></i>
                          <span>{{ getControllerName(controller) }}</span>
                        </div>
                      </div>
                      <div class="controller-item-actions">
                        <button
                          type="button"
                          class="controller-item-settings"
                          title="이름 설정"
                          @click.stop="onControllerNameClick(controller)"
                        >
                          <i class="fa-solid fa-gear"></i>
                        </button>
                        <button
                          type="button"
                          class="controller-item-delete"
                          title="삭제"
                          @click.stop="onControllerDeleteClick(controller)"
                        >
                          <i class="fa-regular fa-trash-can"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="zone-controllers-empty">
                  <i class="fa-solid fa-info-circle me-2"></i>
                  <span>등록된 조명제어기가 없습니다.</span>
                </div>
              </div>
            </div>

            <!-- 하단: 추가/설정 버튼 -->
            <div class="zone-card-footer">
              <div class="row g-2">
                <div class="col-6">
                  <button
                    type="button"
                    class="btn btn-primary w-100"
                    @click.stop="onRegistControllerClick"
                  >
                    추가
                  </button>
                </div>
                <div class="col-6">
                  <button
                    type="button"
                    class="btn btn-outline-primary w-100"
                    @click.stop="onSettingControllerClick"
                    :disabled="!hasController"
                  >
                    설정
                  </button>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
    <ZoneModal
      v-if="zoneModal.visible"
      :visible="zoneModal.visible"
      :okCallback="onUpdateZoneName"
      :cancelCallback="zoneModal.hide"
      :zoneName="props.data?.name"
    />
    <ControllerNameModal
      v-if="controllerNameModal.visible"
      :visible="controllerNameModal.visible"
      :okCallback="onUpdateControllerName"
      :cancelCallback="closeControllerNameModal"
      :serial="editingController?._id || ''"
      :controllerName="editingController?.name || ''"
    />
  </div>
</template>

<script setup>
import { onMounted, defineProps, computed, getCurrentInstance ,reactive, ref} from 'vue';
import Card from '@/common/template/components/bootstrap/Card.vue';
import CardBody from '@/common/template/components/bootstrap/CardBody.vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import Axios, { ZoneApi, CtrlApi } from '@/api';
import useMqttControl from '@/common/hooks/useMqttControl';
import ListGroupItem from '@/common/components/widgets/ListGroupItem.vue';
import ZoneModal from '@/views/rooms/components/ZoneModal.vue';
import ControllerNameModal from './ControllerNameModal.vue';
import useModal from '@/common/hooks/useModal';
import { resolveControllerDisplayName } from '@/common/utils/controllerDisplayName';

const mqttControl = reactive(useMqttControl());
const { emitter } = getCurrentInstance().appContext.config.globalProperties;
const { emit } = getCurrentInstance();
const router = useRouter();
const props = defineProps(['index', 'data', 'onZoneUpdated']);
const store = useStore();
const zoneModal = reactive(useModal());
const controllerNameModal = reactive(useModal());
const editingController = ref(null);
const isExpanded = ref(false); // 기본적으로 접힌 상태
const backColor = computed(() => {
  if (props.index % 5 === 0) {
    return 'bg-gradient-custom-orange';
  } else if (props.index % 5 === 1) {
    return 'bg-gradient-custom-pink';
  } else if (props.index % 5 === 2) {
    return 'bg-gradient-custom-teal';
  } else if (props.index % 5 === 3) {
    return 'bg-gradient-custom-indigo';
  } else {
    return 'bg-gradient-custom-blue';
  }
});

onMounted(() => {
  console.log('ZoneItem.onMounted().props =>', props.data.controllers);
  
  // 현재 선택된 zone인 경우 카드를 기본적으로 펼침
  const currentZone = store.getters['home/getCurrentZone'];
  if (currentZone && currentZone._id === props.data._id) {
    isExpanded.value = true;
  }
});

/** 온도조절기(T01/T02 등)만 제외 — M11 라우터 포함 */
const isTempControllerModel = (model) => {
  if (!model) return false;
  const m = String(model).trim().toUpperCase();
  return m.startsWith('T') && /^T\d+/.test(m);
};

const resolveControllerModel = (controller) => {
  if (controller?.model) return String(controller.model).trim();
  const id = String(controller?._id || '');
  if (id.includes('_')) return id.split('_')[0];
  return '';
};

/** zone.controllers 중 온도조절기만 제외 (M02/M22/M24/M31/M11 등 유지) */
const lightControllers = computed(() => {
  const list = props.data?.controllers || [];
  return list.filter((c) => {
    const model = resolveControllerModel(c);
    // 시리얼에 _ 없는 온도조절기(예: 75DFIS…) / Txx 모델 제외
    if (isTempControllerModel(model)) return false;
    if (!model && !String(c?._id || '').includes('_')) return false;
    return true;
  });
});

const hasController = computed(() => {
  return lightControllers.value.length > 0;
});

const onExpandToggle = () => {
  isExpanded.value = !isExpanded.value;
};

const getControllerName = (controller) => {
  const products = store.getters['home/getProducts'] || [];
  return resolveControllerDisplayName(controller, products, '조명제어기');
};

const getControllerAlias = (controller) => {
  const n = typeof controller?.name === 'string' ? controller.name.trim() : '';
  return n.length > 0 ? n : '';
};

const onControllerNameClick = (controller) => {
  editingController.value = controller;
  controllerNameModal.show();
};

const closeControllerNameModal = () => {
  controllerNameModal.hide();
  editingController.value = null;
};

const onControllerDeleteClick = (controller) => {
  if (!controller?._id) return;
  emitter.emit('showPopConfirm', {
    description: '관련된 장치 등의 모든 데이터가 삭제됩니다.\n조명제어기를 삭제하시겠습니까?',
    okCallback: () => requestDeleteController(controller._id),
  });
};

const requestDeleteController = async (id) => {
  try {
    const result = await Axios.delete(CtrlApi.DELETE_CTRL.replace(':id', id));
    if (result.resultCode === '0000') {
      store.commit('home/deleteController', {
        zoneId: props.data._id,
        controllerId: id,
      });
      try {
        await store.dispatch('home/fetchAllZones');
      } catch (e) {
        console.log(e);
      }
      emit('onZoneUpdated');
      return;
    }
    emitter.emit('showPopAlert', {
      description: result.resultMessage || '조명제어기 삭제에 실패했습니다.',
    });
  } catch (e) {
    emitter.emit('showPopAlert', {
      description: '조명제어기 삭제에 실패했습니다.',
    });
  }
};

const onUpdateControllerName = async (value) => {
  const ctrl = editingController.value;
  if (!ctrl?._id) {
    closeControllerNameModal();
    return;
  }
  const name = typeof value === 'string' ? value.trim() : '';
  try {
    const result = await Axios.patch(
      CtrlApi.UPDATE_CTRL.replace(':id', ctrl._id),
      { name },
    );
    if (result.resultCode === '0000') {
      closeControllerNameModal();
      emit('onZoneUpdated');
      return;
    }
    emitter.emit('showPopAlert', {
      description: result.resultMessage || '제어기 이름 저장에 실패했습니다.',
    });
  } catch (e) {
    emitter.emit('showPopAlert', {
      description: '제어기 이름 저장에 실패했습니다.',
    });
  }
};

const onRegistControllerClick = () => {
  if(!props.data.isEditMode){
    emitter.emit('showPopAlert', {
      title: '세팅 모드',
      description: `세팅모드 화면으로 이동하시겠습니까?\n세팅모드 활성화시 모바일 기기에서 컨트롤이 제한됩니다.`,
      okCallback: async() => {
        const payload = {isEditMode:true}
        const result = await Axios.patch(
          ZoneApi.UPDATE_ZONE.replace(':id', props.data._id),
          payload,
        );
        if (result.resultCode === '0000') {
          mqttControl.onPublishZone(props.data._id,payload)
          router.push(`/settings/zones/${props.data._id}/regist-ble-controllers`);
        }
      },
      cancelCallback:() =>{}
    });
    return;
  }

  router.push(`/settings/zones/${props.data._id}/regist-ble-controllers`);
}
const onSettingControllerClick = () =>{
  if (!hasController.value) {
    emitter.emit('showPopAlert', {
      description: '등록된 조명제어기가 없습니다.\n먼저 조명제어기를 등록해주세요.',
    });
    return;
  }
  router.push(`/settings/zones/${props.data._id}/setting-ble-controllers`);
}

const onControllerSelect = (controller) => {
  if (!controller?._id) return;
  const list = lightControllers.value || [];
  const isLast = list.length > 0 && list[list.length - 1]?._id === controller._id;
  router.push({
    path: `/settings/zones/${props.data._id}/setting-ble-controllers`,
    query: {
      controllerId: controller._id,
      ...(isLast ? { scrollEnd: '1' } : {}),
    },
  });
};

const onRefreshClick = () => {
  emitter.emit('showLoading');
  emit('onZoneUpdated');
  setTimeout(() => emitter.emit('hideLoading'), 3000);
};

const onDeleteClick = () => {
  console.log('ZoneItem.onDeleteClick =>', {
    name: props.data.name,
    zoneId: props.data._id,
  });

  emitter.emit('showPopConfirm', {
    title: '영역 삭제',
    description: `${props.data.name} 영역을 삭제하시겠습니까?`,
    okText: "삭제",
    cancelText: '취소',
    okCallback: () => setTimeout(() => emit('onDeleteClick', { name: props.data.name, zoneId: props.data._id }), 100)
  });
};

const onTouchHold = () => {
  zoneModal.show();
}

const onUpdateZoneName = (value) => {
  requestUpdateZone({
    name: value,
  });
  zoneModal.hide();
};

const requestUpdateZone = async (params) => {
  try {
    const result = await Axios.patch(
      ZoneApi.UPDATE_ZONE.replace(':id', props.data._id),
      params,
    );
    if (result.resultCode === '0000') {
      store.commit('home/updateZone', {
        id: props.data._id,
        data: params,
      });
      emit('onZoneUpdated');
    } else {
      emitter.emit('showPopAlert', {
        description: result.resultMessage,
      });
    }
  } catch (error) {
    emitter.emit('showPopAlert', {
      description: '영역 정보 수정에 실패했습니다.',
    });
  }
};
</script>

<style scoped>
.zone-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  max-width: 500px;
  margin: 0 auto;
}

.zone-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.zone-card-content {
  padding: 1rem;
}

.zone-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  cursor: pointer;
}

.zone-expand-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  transition: all 0.2s ease;
}

.zone-expand-toggle:hover {
  color: #212529;
}

.zone-card-expandable-content {
  overflow: hidden;
}

.zone-title-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
}

.zone-header-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.zone-icon {
  color: #0d6efd;
  font-size: 1rem;
}

.zone-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #212529;
}

.zone-card-body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.zone-info-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 1rem;
  font-size: 0.75rem;
  color: #495057;
  font-weight: 500;
  white-space: nowrap;
}

.zone-info-badge.badge-warning {
  background-color: #fff3cd;
  border-color: #ffc107;
  color: #856404;
}

.zone-info-badge.badge-normal {
  background-color: #d1e7dd;
  border-color: #198754;
  color: #0f5132;
}

.zone-info-badge i {
  font-size: 0.8rem;
}

.zone-warning {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.5rem;
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 0.5rem;
  color: #856404;
  font-size: 0.8rem;
  margin-top: 0.5rem;
}

.zone-warning i {
  color: #ffc107;
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.zone-warning span {
  line-height: 1.5;
}

.zone-card-footer {
  padding: 0.75rem 1rem;
  border-top: 1px solid #e9ecef;
  margin-top: 0.5rem;
}

.zone-card-footer .btn {
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
  font-weight: 500;
  border-radius: 0.5rem;
}

.zone-controllers-section {
  margin-top: 0.75rem;
  padding-top: 0;
}

.zone-controllers-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #212529;
}

.zone-controllers-refresh {
  margin-left: auto;
  border: none;
  background: transparent;
  padding: 0.2rem 0.35rem;
  color: #6c757d;
  line-height: 1;
  cursor: pointer;
}

.zone-controllers-refresh:hover {
  color: #0d6efd;
}

.zone-controllers-refresh:focus {
  outline: none;
}

.zone-controllers-list {
  margin-top: 0.5rem;
}

.zone-controllers-title {
  margin-right: 0.5rem;
}

.zone-controllers-count {
  color: #6c757d;
  font-weight: 400;
  font-size: 0.85rem;
}

.zone-controllers-items {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.zone-controller-item {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 0.75rem;
  transition: all 0.2s ease;
  cursor: pointer;
}

.zone-controller-item:hover {
  background-color: #e9ecef;
  border-color: #dee2e6;
}

.controller-item-main {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
}

.controller-item-info {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  flex: 1;
  min-width: 0;
}

.controller-item-serial-row {
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
  flex-wrap: wrap;
  min-width: 0;
}

.controller-item-serial {
  display: inline-flex;
  align-items: center;
  font-size: 0.9rem;
  font-weight: 700;
  color: #212529;
  word-break: break-all;
}

.controller-item-alias {
  font-size: 0.85rem;
  font-weight: 600;
  color: #0d6efd;
  white-space: nowrap;
}

.controller-item-model {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  font-weight: 500;
  color: #6c757d;
  min-width: 0;
}

.controller-item-model span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.controller-item-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0.15rem;
}

.controller-item-settings,
.controller-item-delete {
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: #6c757d;
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}

.controller-item-settings:hover {
  color: #0d6efd;
  background-color: #e7f1ff;
}

.controller-item-delete:hover {
  color: #dc3545;
  background-color: #f8d7da;
}

.zone-controllers-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  color: #6c757d;
  font-size: 0.85rem;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
}

/* 확장/축소 애니메이션 */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
  margin-top: 0;
  padding-top: 0;
}

.expand-enter-to,
.expand-leave-from {
  opacity: 1;
  max-height: 1000px;
}
</style>
